import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { LocationFilter, SortKey } from '../types';
import { CategoryFilterProps, ServiceCategorySelect } from './ServiceCategorySelect';

export interface LocationOption {
  value: LocationFilter;
  label: string;
  count: number;
}

export interface TeamFiltersProps {
  search: string;
  onSearch: (value: string) => void;
  sortKey: SortKey;
  onSort: (key: SortKey) => void;
  showInactive: boolean;
  onToggleInactive: (value: boolean) => void;
  inactiveCount: number;
  searchPlaceholder?: string;
  /** Admin only: a location switch above the filter row. */
  locationOptions?: LocationOption[];
  location?: LocationFilter;
  onLocation?: (value: LocationFilter) => void;
  /** Service-category multi-select, between sort and the inactive toggle. */
  categoryFilter?: CategoryFilterProps;
}

const SORT_OPTIONS: Array<{ value: SortKey; label: string; adminOnly?: boolean }> = [
  { value: 'sessions', label: 'Sessions · latest 6 months' },
  { value: 'change', label: 'Change vs prior' },
  { value: 'net', label: 'Net client change' },
  { value: 'conversion', label: 'Conversion rate' },
  { value: 'overdue', label: 'Overdue updates' },
  { value: 'name', label: 'Name' },
  { value: 'location', label: 'Location', adminOnly: true },
];

/** One filter row (search, sort, inactive toggle) that scopes every table below it, plus the admin location tabs. */
export const TeamFilters: React.FC<TeamFiltersProps> = ({
  search,
  onSearch,
  sortKey,
  onSort,
  showInactive,
  onToggleInactive,
  inactiveCount,
  searchPlaceholder = 'Find a team member',
  locationOptions,
  location = 'ALL',
  onLocation,
  categoryFilter,
}) => (
  <Box>
    {locationOptions && onLocation ? (
      <Tabs
        value={location}
        onChange={(_event, value: LocationFilter) => onLocation(value)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="Location"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { minWidth: 0 } }}
      >
        {locationOptions.map((option) => (
          <Tab key={option.value} value={option.value} label={`${option.label} · ${option.count}`} />
        ))}
      </Tabs>
    ) : null}
    <Stack
      role="toolbar"
      aria-label="Table filters"
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      alignItems={{ md: 'center' }}
      useFlexGap
      flexWrap="wrap"
    >
      <TextField
        id="staff-performance-search"
        size="small"
        value={search}
        onChange={(event) => onSearch(event.target.value)}
        placeholder={searchPlaceholder}
        inputProps={{ 'aria-label': searchPlaceholder }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ minWidth: 260 }}
      />
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel id="staff-performance-sort-label">Sort</InputLabel>
        <Select
          labelId="staff-performance-sort-label"
          id="staff-performance-sort"
          label="Sort"
          value={sortKey}
          onChange={(event) => onSort(event.target.value as SortKey)}
        >
          {SORT_OPTIONS.filter((option) => !option.adminOnly || locationOptions).map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {categoryFilter && (categoryFilter.options.length > 0 || categoryFilter.selected !== null) ? (
        <ServiceCategorySelect {...categoryFilter} />
      ) : null}
      <FormControlLabel
        control={
          <Switch
            id="staff-performance-show-inactive"
            checked={showInactive}
            onChange={(event) => onToggleInactive(event.target.checked)}
            size="small"
          />
        }
        label={`Show inactive (${inactiveCount})`}
        sx={{ ml: 0.5 }}
      />
    </Stack>
  </Box>
);
